/*eslint react-hooks/exhaustive-deps: "off"*/
import React, { useEffect, useState } from "react";
import MapBase from "./MapBase";
import useMapProps from "./useMapProps";
import { MapPropsProvider } from "./MapPropsContext";
import WidgetContainer from "./widgets/WidgetContainer";
import { getApiUrl } from "../../utils";
import WidgetTableClicked from "./widgets/WidgetTableClicked";
import WidgetTable from "./widgets/WidgetTable";
import WidgetVisibleRadius from "./widgets/WidgetVisibleRadius";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetGeocoding from "./widgets/WidgetGeocoding";
import WidgetToolbar from "./widgets/WidgetToolbar";
import AlignCenterText from "./widgets/AlignCenterText";
import { message } from "antd";
import WidgetWifi, { OPTIONS_WIFI } from "./widgets/WidgetWifi";
import useVworldCityType from "./useVworldCityType";
import { TI_WIDGET_TABLE } from "../../Constants";
import EllipsisText from "./widgets/EllipsisText";
import { WidgetButton } from "../../styled-components/Button";

const INITIAL_VIEW_STATE = {
  latitude: 37.496256,
  longitude: 127.062191,
  zoom: 16,
};

const ICON_MAPPING = {
  공공: ["공공wifi", "공공"],
  지자체: ["공공wifi", "지자체"],
  SKT: ["통신사wifi", "SKT"],
  KT: ["통신사wifi", "KT"],
  LGT: ["통신사wifi", "LGT"],
};

/*
//와이파이 지도 아이콘 변경. 2024-11-07 MHS
const ICON_MAPPING = {
  공공: ["공공wifi", "공공"],
  지자체: ["공공wifi", "지자체"],
  SKT: ["5g-sk-out-none", "SKT"],
  KT: ["5g-kt-out-none", "KT"],
  LGT: ["5g-lg-out-none", "LGT"],
};
*/

message.config({
  top: 250,
  duration: 15,
  maxCount: 1,
});

const infoByZoom = () => {
  message.info({
    content: (
      <span style={{ fontSize: 15 }}>
        {" "}
        해당 zoom에서는 검색이 불가합니다. 확대해 주시기 바랍니다.
      </span>
    ),
  });
};

const MapWifi = () => {
  const [isTableVisible, setTableVisible] = useState(false);
  const mapProps = useMapProps({
    INITIAL_VISIBLE_RADIUS: 500,
    INITIAL_VIEW_STATE,
    INITIAL_POINT_LAYER_PROPS: {
      getIconCode: ({ ccb }) => {
        try {
          return ICON_MAPPING[ccb][0];
        } catch (e) {
          // console.error("not found iconCode for", ccb);
          return null;
        }
      },
      getIconWidth: (feature, zoom) => {
        let iconWidth = 30;
        if (zoom <= 12) {
          iconWidth -= (iconWidth * (15 - Math.max(7, zoom))) / 11;
          return Math.min(25.2, iconWidth);
        } else if (zoom <= 13) {
          iconWidth -= (iconWidth * (15 - Math.max(7, zoom))) / 5;
          return Math.min(25.2, iconWidth);
        } else if (zoom <= 14) {
          iconWidth -= (iconWidth * (15 - Math.max(7, zoom))) / 3;
          return Math.min(25.2, iconWidth);
        } else if (zoom === 15) {
          iconWidth -= (iconWidth * (16 - Math.max(7, zoom))) / 4;
          return Math.min(30, iconWidth);
        } else if (zoom >= 16) {
          return 30;
        }
      },
    },
    INITIAL_LAYER_WMS_TYPES: [],
    INITIAL_WIDGET_STATE: {
      visible_radius: 500,
      wifi_code: OPTIONS_WIFI.map(({ value }) => value),
    },
  });

  const {
    viewState,
    clickedPointIconObjects,
    widgetState,
    setPointsLayerFilterFunc,
    setVisibleRadius,
    setIsClusteringPoint,
  } = mapProps;

  const { latitude, longitude, zoom } = viewState;
  const [vworldCityType, setVworldCityType] = useState();

  useEffect(() => {
    if (zoom < 13) {
      infoByZoom();
    } else if (zoom >= 13) {
      message.destroy();
    }
    // zoom이 15이하일 때는 클러스터링 보기, 초과부터 아이콘 보기
    if (zoom <= 15) {
      setIsClusteringPoint(true);
    } else if (zoom > 15) {
      setIsClusteringPoint(false);
    }
  }, [zoom]);

  useEffect(() => {
    const { visible_radius, wifi_code } = widgetState;
    setPointsLayerFilterFunc(
      () =>
        ({ ccb }) =>
          wifi_code
            .map((row) => row.split(","))
            .flat()
            .includes(ccb)
    );
    setVisibleRadius(visible_radius);
  }, [widgetState]);

  const { setPosition: setPositionForCityType } = useVworldCityType(
    (cityType) => {
      setVworldCityType(cityType);
    }
  );

  useEffect(() => {
    setPositionForCityType({ latitude, longitude });
  }, [clickedPointIconObjects]);

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase>
        <WidgetContainer>
          <div>
            <WidgetGeocoding 
              onLocationSelectTriggerTable={() => setTableVisible(true)}
            />
            <WidgetVisibleRadius />
            <WidgetWifi />
          </div>
        </WidgetContainer>

        <WidgetTableClicked
          fetchUrl={getApiUrl({ type: "type2" })}
          getTitle={(objects) => `${objects.length}개의 와이파이`}
          objects={clickedPointIconObjects}
          getRowTitle={({ zone_nm }) => zone_nm}
          getRow={({ zone_nm, address, ccb }) => (
            <table>
              <tbody>
                <tr>
                  <th>와이파이명</th>
                  <td>{zone_nm}</td>
                </tr>
                <tr>
                  <th>제공기관</th>
                  <td>{ccb}</td>
                </tr>
                <tr>
                  <th>설치 장소</th>
                  <td>{address}</td>
                </tr>
                <tr>
                  <th>용도지역정보</th>
                  <td>{vworldCityType}</td>
                </tr>
              </tbody>
            </table>
          )}
        />

        <WidgetTable
          visible={isTableVisible}
          setVisible={setTableVisible}
          fetchUrl={getApiUrl({ type: "type1" })}
          getTitle={(layer) => `${layer.length}개의 와이파이`}
          isUidSearch={true}
          widgetTabIndex={TI_WIDGET_TABLE}
          columns={[
            {
              title: "순번",
              dataIndex: "index",
              key: "index",
              width: 75,
              fixed: "left",
              align: "center",
              // sorter: makeTableSorterFn("index"),
            },
            {
              title: "와이파이명",
              dataIndex: "zone_nm",
              key: "zone_nm",
              width: 300,
              fixed: "left",
              // sorter: makeTableSorterFn("zone_nm"),
            },
            {
              title: "제공기관",
              dataIndex: "ccb",
              key: "ccb",
              width: 100,
              render: (text) => AlignCenterText({ text }),
              // sorter: makeTableSorterFn("ccb"),
            },
            {
              title: "설(상)치장소",
              dataIndex: "address",
              key: "address",
              render: (address) => (
                <WidgetButton>
                  <EllipsisText text={address} />
                </WidgetButton>
              ),
              // sorter: makeTableSorterFn("address"),
            },
            {
              title: "위도",
              dataIndex: "lat",
              key: "latitude",
              width: 140,
              render: (text) => AlignCenterText({ text }),
            },
            {
              title: "경도",
              dataIndex: "lon",
              key: "longitude",
              width: 140,
              render: (text) => AlignCenterText({ text }),
            },
          ]}
          horizontalScroll={1400}
        />

        <WidgetLegend />
      </MapBase>
      <WidgetToolbar />
    </MapPropsProvider>
  );
};

export default MapWifi;
